'use client'
import { RVOL_MIN_SAMPLES } from '@/lib/rvol/rvolThresholds'

/**
 * StockLivePulse — the live heart of /stocks/[symbol] (owner 2026-07-07):
 * "a place where people can hang out, watch the stock going up and discuss live."
 *
 *  · Metrics strip: big price (flashes green/red on ticks), % pill, $ change, day
 *    volume, live dot + last-update clock. Quotes poll every 10s.
 *  · Mini EMA chart: 1-minute price line with OUR 9/50/200 EMAs (same palette as the
 *    full chart: blue/yellow/green), previous-close dashed baseline, gradient fill
 *    colored by the day's direction. Bars poll every 15s — watchers see it move.
 *
 * SEO-safe: the page still server-renders price context; this hydrates it live.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import ChartStatsReadout from '@/components/ChartStatsReadout'
import { applyMinichart, minichartSince, minichartUrl, type MinichartState } from '@/lib/minichartMerge'
import Link from 'next/link'
import { Bell, Book, CandlestickChart, TrendingDown, TrendingUp } from 'lucide-react'
import { useTheme } from 'next-themes'

type Bar = { t: number; o: number; h: number; l: number; c: number; v: number;
  /** expected volume for this bar's minutes, from the shared baseline (may be absent) */
  baseVol?: number; baseN?: number }
type Session = 'pre' | 'rth' | 'post' | 'closed'
export type InitialQuote = Required<Quote>

type Quote = { price: number | null; chg: number | null; chgPct: number | null; volume: number | null; dayHigh: number | null; dayLow: number | null; session?: Session; regClose?: number | null; prevClose?: number | null }

// Roll the 1-minute bars up into wider, readable candles (open=first, close=last, high/low extremes, volume summed).
function aggregateCandles(bars: Bar[], bucketMs: number): Bar[] {
  if (!bars.length) return []
  const map = new Map<number, Bar>()
  for (const b of bars) {
    const key = Math.floor(b.t / bucketMs)
    const cur = map.get(key)
    if (!cur) map.set(key, { t: key * bucketMs, o: b.o, h: b.h, l: b.l, c: b.c, v: b.v || 0, baseVol: b.baseVol, baseN: b.baseN })
    else {
      if (b.h > cur.h) cur.h = b.h; if (b.l < cur.l) cur.l = b.l; cur.c = b.c; cur.v += b.v || 0
      // baseVol sums exactly like volume — that is why the API ships the expectation, not the ratio.
      // baseN takes the WEAKEST minute in the bucket: one 1-session minute makes the whole candle's
      // denominator unreliable, and averaging would hide precisely that.
      if (b.baseVol != null) cur.baseVol = (cur.baseVol ?? 0) + b.baseVol
      if (b.baseN != null) cur.baseN = cur.baseN == null ? b.baseN : Math.min(cur.baseN, b.baseN)
    }
  }
  return [...map.values()].sort((a, b) => a.t - b.t)
}

const EMA_DEFS = [
  { period: 9, color: '#3b82f6' },
  { period: 50, color: '#eab308' },
  { period: 200, color: '#22c55e' },
] as const

const nCompact = (n: number) =>
  n >= 1e9 ? `${(n / 1e9).toFixed(1)}B` : n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(0)}K` : String(Math.round(n))
const fmtPx = (n: number) => (n < 1 ? n.toFixed(4) : n.toFixed(2))

/** RVOL for a bar: actual volume over what this minute-of-session normally does.
 *
 *  Null (a GAP in the pane, never a 0x bar) when there is no usable expectation. Zero-volume bars are
 *  excluded deliberately: they are almost always the synthetic gap-fill candles aggregateCandles
 *  inserts to keep the axis continuous on an illiquid name, so "0x" would describe our own padding
 *  rather than the tape. Kept identical to the full chart's rvolOf so the two panes cannot disagree. */
function rvolOf(c: { v: number; baseVol?: number }): number | null {
  if (!c.baseVol || c.baseVol <= 0) return null
  if (!Number.isFinite(c.v) || c.v <= 0) return null
  return c.v / c.baseVol
}
/** Under this many sampled sessions the denominator is too thin to trust, and the bar is drawn washed
 *  out. The baseline is weakest on brand-new movers, which are exactly the names people come to look
 *  at, so the UI has to admit it rather than present a 6-session average as authoritative. */
// ⛔ WAS A HAND-COPIED `= 10`. rvolBaseline.ts imports `db`, so this client file could not reach
// the real constant and kept its own. Three copies of one number: see rvol/rvolThresholds.ts.
// (imported at the top of this file)

function emaSeries(closes: number[], period: number): number[] {
  const k = 2 / (period + 1)
  const out: number[] = [closes[0]]
  for (let i = 1; i < closes.length; i++) out.push(closes[i] * k + out[i - 1] * (1 - k))
  return out
}

export default function StockLivePulse({
  symbol,
  initialPrice,
  initialPrevClose,
  initialQuote,
}: {
  symbol: string
  initialPrice: number | null
  initialPrevClose: number | null
  /** The server snapshot, same mapping as the first poll (snapshotQuote), so the metrics row is complete at SSR. */
  initialQuote?: InitialQuote
}) {
  const [quote, setQuote] = useState<Quote>(() => initialQuote
    ? { price: initialQuote.price, chg: initialQuote.chg, chgPct: initialQuote.chgPct, volume: initialQuote.volume, dayHigh: initialQuote.dayHigh, dayLow: initialQuote.dayLow, session: initialQuote.session, regClose: initialQuote.regClose, prevClose: initialQuote.prevClose }
    : {
        price: initialPrice,
        chg: initialPrice != null && initialPrevClose ? initialPrice - initialPrevClose : null,
        chgPct: initialPrice != null && initialPrevClose ? ((initialPrice - initialPrevClose) / initialPrevClose) * 100 : null,
        volume: null,
        dayHigh: null,
        dayLow: null,
      })
  const [bars, setBars] = useState<Bar[]>([])
  const [prevClose, setPrevClose] = useState<number | null>(initialPrevClose)
  const [flash, setFlash] = useState<'up' | 'down' | null>(null)
  const [updatedAt, setUpdatedAt] = useState<string>('')
  const prevPriceRef = useRef<number | null>(initialPrice)
  const [hover, setHover] = useState<number | null>(null)
  // ── ZOOM / PAN (owner 2026-07-28) ────────────────────────────────────────────────────────────────
  // The intraday tab had a FIXED 90-candle window and no navigation at all. Held as bar COUNTS, not
  // pixels, so a resize never changes what you are looking at.
  const [zoom, setZoom] = useState(1)      // >1 = fewer candles on screen = zoomed in
  const [panEnd, setPanEnd] = useState(0)  // candles trimmed from the RIGHT edge (0 = latest)
  const paneRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<{ x: number; panEnd: number } | null>(null)
  // The SVG chart paints its own chrome (gridlines etc), so it reads the resolved theme and branches;
  // the DARK value at each site is kept verbatim so dark stays byte-identical. Data colors (candles,
  // EMAs, RVOL) read on either ground and stay inline.
  const { resolvedTheme } = useTheme()
  const dark = resolvedTheme !== 'light'

  const fetchQuote = useCallback(async () => {
    const q = await fetch(`/api/watchlist/quotes?symbols=${encodeURIComponent(symbol)}`, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null)).catch(() => null)
    const it = Array.isArray(q?.quotes) ? q.quotes[0] : null
    if (!it) return
    const prev = prevPriceRef.current
    if (it.price != null && prev != null && it.price !== prev) {
      setFlash(it.price > prev ? 'up' : 'down')
      setTimeout(() => setFlash(null), 900)
    }
    if (it.price != null) prevPriceRef.current = it.price
    setQuote({ price: it.price, chg: it.chg, chgPct: it.chgPct, volume: it.volume, dayHigh: it.dayHigh ?? null, dayLow: it.dayLow ?? null, session: it.session, regClose: it.regClose ?? null, prevClose: it.prevClose ?? null })
    setUpdatedAt(new Date().toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true }))
  }, [symbol])

  // Incremental: after the first full load each 10s poll asks only for the newest bars (src/lib/minichartMerge.ts).
  // Keyed by symbol so a client-side hop to another ticker never merges onto the last one.
  const mcRef = useRef<{ symbol: string; state?: MinichartState<Bar> }>({ symbol })
  const fetchBars = useCallback(async () => {
    if (mcRef.current.symbol !== symbol) mcRef.current = { symbol }
    const now = Date.now()
    const since = minichartSince(mcRef.current.state, now)
    const j = await fetch(minichartUrl(symbol, since), { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null)).catch(() => null)
    if (mcRef.current.symbol !== symbol) return
    const next = applyMinichart(mcRef.current.state, j, since, now)
    if (next && next !== mcRef.current.state) { mcRef.current.state = next; setBars(next.bars) }
    if (j?.prevClose != null) setPrevClose(Number(j.prevClose))
  }, [symbol])

  // ── WHEEL ZOOM: NATIVE AND NON-PASSIVE ──────────────────────────────────────────────────────────
  // React attaches onWheel PASSIVELY, and preventDefault() in a passive listener is ignored, so the
  // handler runs and the page scrolls anyway — exactly the reported symptom. addEventListener with
  // { passive: false } is the only way to claim the gesture.
  useEffect(() => {
    const el = paneRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      setZoom((z) => Math.min(8, Math.max(1, z * (e.deltaY < 0 ? 1.15 : 1 / 1.15))))
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [])

  useEffect(() => {
    fetchQuote(); fetchBars()
    const q = setInterval(() => { if (!document.hidden) fetchQuote() }, 10_000)
    const b = setInterval(() => { if (!document.hidden) fetchBars() }, 10_000)
    return () => { clearInterval(q); clearInterval(b) }
  }, [fetchQuote, fetchBars])

  const up = (quote.chgPct ?? 0) >= 0
  const lineColor = up ? '#34d399' : '#fb7185'

  // ── Session-aware breakdown: the REGULAR daily % (regular close vs prev close) + the pre/post-market
  //    EXTENDED move (current vs the regular close, or vs prev close pre-market), with a session badge. ──
  const sess = quote.session
  const pc = quote.prevClose ?? prevClose
  const rc = quote.regClose ?? null
  const regularPct = (sess === 'post' || sess === 'closed') && rc != null && pc != null && pc > 0 ? ((rc - pc) / pc) * 100 : null
  const extBase = sess === 'pre' ? pc : rc // pre-market: vs prev close (no regular session yet); after-hours: vs the regular close
  const extPct = (sess === 'pre' || sess === 'post') && quote.price != null && extBase != null && extBase > 0 ? ((quote.price - extBase) / extBase) * 100 : null
  const sessBadge = sess === 'pre' ? { label: 'Pre-market', cls: 'text-sky-700 dark:text-sky-300 bg-sky-500/10 ring-sky-500/25' }
    : sess === 'post' ? { label: 'After-hours', cls: 'text-violet-700 dark:text-violet-300 bg-violet-500/10 ring-violet-500/25' }
    : sess === 'closed' ? { label: 'Market closed', cls: 'text-muted-foreground bg-muted ring-border' }
    : null

  // ── chart geometry: 5-min candles for readability; EMAs computed over the full candle series ──
  const candlesAll = aggregateCandles(bars, 5 * 60_000)
  const DISPLAY = Math.max(20, Math.round(90 / zoom))   // floor so zooming in cannot collapse to one candle
  const allCloses = candlesAll.map((c) => c.c)
  const emas = allCloses.length > 1 ? EMA_DEFS.map((d) => ({ ...d, series: emaSeries(allCloses, d.period) })) : []
  // panEnd slides the window back through history; clamped so a full window always fits.
  const viewEnd = Math.max(DISPLAY, candlesAll.length - panEnd)
  const view = candlesAll.slice(Math.max(0, viewEnd - DISPLAY), viewEnd)
  const viewEmas = emas.map((e) => ({ ...e, view: e.series.slice(Math.max(0, viewEnd - DISPLAY), viewEnd) }))
  const lastClose = view.length ? view[view.length - 1].c : null
  const maxVol = view.length ? Math.max(1, ...view.map((c) => c.v)) : 1

  // RVOL axis top: a high percentile of the plotted bars, NOT the max, floored at 3x.
  //
  // Scaling to the max lets a single 40x print drag the axis up and squash the 1-3x band -- where
  // essentially every bar lives -- flat against the floor. Bars above the cap are CLIPPED at the top,
  // which reads as "off the scale"; they are not hidden, and the header always shows the true multiple.
  //
  // ⚠ THE PERCENTILE HAS TO SUIT THE BAR COUNT. The full chart uses p99 because it plots ~1200 bars,
  // where p99 discards the top dozen. This pane plots ~90, and floor(90 * 0.99) = 89 is the LAST INDEX
  // -- so p99 there is exactly the maximum and the outlier protection silently does nothing. Shipped
  // that way first and one spike flattened the whole pane. p90 discards the top ~9 of 90, which is the
  // same INTENT at this bar count rather than the same NUMBER.
  //
  // Null when no bar in view has a usable expectation, which hides the pane rather than drawing an
  // empty box.
  const rvolPane = (() => {
    const vals: number[] = []
    for (const c of view) { const r = rvolOf(c); if (r != null && Number.isFinite(r)) vals.push(r) }
    if (!vals.length) return null
    vals.sort((a, b) => a - b)
    const p90 = vals[Math.max(0, Math.min(vals.length - 1, Math.floor(vals.length * 0.90) - 1))]
    return { cap: Math.max(3, p90) }
  })()
  // Scale to the PRICE action so candles fill the pane (owner 2026-07-15: "zoom in"). EMAs pull the range
  // out only half the price range on each side, so a far 200-EMA never squishes the candles; beyond that
  // the EMA line is clamped to the pane edge when drawn.
  const priceVals = view.flatMap((c) => [c.h, c.l])
  const pMin = priceVals.length ? Math.min(...priceVals) : 0
  const pMax = priceVals.length ? Math.max(...priceVals) : 1
  const pR = (pMax - pMin) || 1
  let yMin = pMin, yMax = pMax
  for (const e of viewEmas) if (allCloses.length > e.period) for (const v of e.view) { if (v < yMin) yMin = v; if (v > yMax) yMax = v }
  if (prevClose != null && prevClose >= pMin - pR && prevClose <= pMax + pR) { yMin = Math.min(yMin, prevClose); yMax = Math.max(yMax, prevClose) }
  yMin = Math.max(yMin, pMin - pR * 0.5)
  yMax = Math.min(yMax, pMax + pR * 0.5)
  const span = yMax - yMin || 1
  const W = 640, H = 210
  const n = Math.max(view.length, 1)
  const cx = (i: number) => ((i + 0.5) / n) * W
  const cw = Math.max(1.4, (W / n) * 0.62)
  const Y = (v: number) => H - 6 - ((v - yMin) / span) * (H - 12)
  const Yc = (v: number) => Math.max(6, Math.min(H - 6, Y(v)))
  const pathOf = (vals: number[]) => vals.map((v, i) => `${i === 0 ? 'M' : 'L'}${cx(i).toFixed(1)},${Y(v).toFixed(1)}`).join(' ')
  const pathOfClamped = (vals: number[]) => vals.map((v, i) => `${i === 0 ? 'M' : 'L'}${cx(i).toFixed(1)},${Yc(v).toFixed(1)}`).join(' ')

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-lg ring-1 ring-border">
      {/* ── Metrics strip: price + direction · % pill · $ chg · day-range bar · vol ── */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-border px-4 py-3">
        <span className="flex items-baseline gap-2">
          <span className={`font-mono text-3xl font-bold tabular-nums transition-colors duration-500 ${
            flash === 'up' ? 'text-emerald-600 dark:text-emerald-300' : flash === 'down' ? 'text-rose-600 dark:text-rose-300' : 'text-foreground'
          }`}>
            {quote.price != null ? `$${fmtPx(quote.price)}` : '—'}
          </span>
          {up ? <TrendingUp className="h-5 w-5 self-center text-emerald-600 dark:text-emerald-400" /> : <TrendingDown className="h-5 w-5 self-center text-rose-600 dark:text-rose-400" />}
        </span>
        {/* % and $ change SEPARATED: pill carries the %, the $ figure reads beside it */}
        {quote.chgPct != null && (
          <span className="flex items-center gap-2">
            <span className={`rounded-md px-2 py-0.5 font-mono text-sm font-bold tabular-nums ${
              up ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-500/25' : 'bg-rose-500/15 text-rose-700 dark:text-rose-300 ring-1 ring-rose-500/25'
            }`}>
              {up ? '+' : ''}{quote.chgPct.toFixed(2)}%
            </span>
            {quote.chg != null && (
              <span className={`font-mono text-sm font-semibold tabular-nums ${up ? 'text-emerald-600 dark:text-emerald-400/90' : 'text-rose-600 dark:text-rose-400/90'}`}>
                {quote.chg >= 0 ? '+' : '−'}${fmtPx(Math.abs(quote.chg))}
              </span>
            )}
          </span>
        )}
        {/* Session badge + regular-daily / extended-hours split (owner request: daily %, pre/post badge, extended %). */}
        {sessBadge && (
          <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ring-1 ${sessBadge.cls}`}>
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-current opacity-70" />{sessBadge.label}
          </span>
        )}
        {regularPct != null && (
          <span className="font-mono text-xs text-muted-foreground">Day <span className={`tabular-nums ${regularPct >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>{regularPct >= 0 ? '+' : ''}{regularPct.toFixed(2)}%</span></span>
        )}
        {extPct != null && (
          <span className="font-mono text-xs text-muted-foreground">{sess === 'pre' ? 'Pre-mkt' : 'After-hrs'} <span className={`tabular-nums ${extPct >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>{extPct >= 0 ? '+' : ''}{extPct.toFixed(2)}%</span></span>
        )}
        {/* Day range: where the price sits between today's low and high */}
        {quote.dayLow != null && quote.dayHigh != null && quote.dayHigh > quote.dayLow && quote.price != null && (
          <span className="flex items-center gap-2 font-mono text-[10px] text-muted-foreground">
            <span className="tabular-nums">${fmtPx(quote.dayLow)}</span>
            <span className="relative h-1 w-24 overflow-hidden rounded-full bg-muted">
              <span className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-rose-500/60 via-amber-400/60 to-emerald-500/60"
                style={{ width: `${Math.min(100, Math.max(0, ((quote.price - quote.dayLow) / (quote.dayHigh - quote.dayLow)) * 100))}%` }} />
              <span className="absolute top-1/2 h-2.5 w-0.5 -translate-y-1/2 rounded-full bg-foreground"
                style={{ left: `calc(${Math.min(100, Math.max(0, ((quote.price - quote.dayLow) / (quote.dayHigh - quote.dayLow)) * 100))}% - 1px)` }} />
            </span>
            <span className="tabular-nums">${fmtPx(quote.dayHigh)}</span>
          </span>
        )}
        {quote.volume != null && quote.volume > 0 && (
          <span className="font-mono text-xs text-muted-foreground">Vol <span className="text-foreground">{nCompact(quote.volume)}</span></span>
        )}
        {prevClose != null && (
          <span className="font-mono text-xs text-muted-foreground">Prev ${fmtPx(prevClose)}</span>
        )}
        <span className="ml-auto flex items-center gap-2">
          <span className="mr-1 flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
            LIVE
            {/* The time only exists after mount, so it used to widen this right-aligned group by ~89px and shift the
                row (ticker-hero-price-block-residual-cls-1102). The invisible widest-case copy shares the grid cell, so
                the width is reserved from the first paint. */}
            <span className="inline-grid tabular-nums">
              <span aria-hidden="true" className="invisible col-start-1 row-start-1">&nbsp;· 12:00:00 AM ET</span>
              <span className="col-start-1 row-start-1">{updatedAt ? <>&nbsp;· {updatedAt} ET</> : null}</span>
            </span>
          </span>
          {/* Action badges (replaced the clunky link list that sat at the page bottom) */}
          <Link href={`/chart/${encodeURIComponent(symbol)}`} target="_blank" rel="noopener noreferrer nofollow"
            className="inline-flex items-center gap-1.5 rounded-lg border border-amber-600/40 bg-amber-500/10 px-2.5 py-1.5 text-xs font-semibold text-amber-700 dark:text-amber-300 transition-colors hover:bg-amber-500/20">
            <CandlestickChart className="h-3.5 w-3.5" /> Full chart
          </Link>
          <Link prefetch={false} href="/alerts"
            className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-600/40 bg-emerald-500/10 px-2.5 py-1.5 text-xs font-semibold text-emerald-700 dark:text-emerald-300 transition-colors hover:bg-emerald-500/20">
            <Bell className="h-3.5 w-3.5" /> Live alerts
          </Link>
          <Link prefetch={false} href="/guides"
            className="hidden items-center gap-1.5 rounded-lg border border-border bg-muted px-2.5 py-1.5 text-xs font-semibold text-foreground/80 transition-colors hover:bg-accent dark:hover:bg-zinc-700/60 sm:inline-flex">
            <Book className="h-3.5 w-3.5" /> Guides
          </Link>
        </span>
      </div>

      {/* ── Mini candlestick chart ── */}
      {view.length > 2 ? (
        <>
          <div
            className="relative"
            ref={paneRef}
            onMouseMove={(e) => {
              const rect = e.currentTarget.getBoundingClientRect()
              const frac = (e.clientX - rect.left) / rect.width
              setHover(Math.min(n - 1, Math.max(0, Math.floor(frac * n))))
              // Drag to pan. Pixels converted to CANDLES against the current window, so one candle of
              // cursor travel moves exactly one candle at any zoom level or width.
              const d = dragRef.current
              if (d && n > 0) {
                const moved = Math.round(((e.clientX - d.x) / rect.width) * n)
                setPanEnd(Math.max(0, Math.min(Math.max(0, candlesAll.length - 20), d.panEnd + moved)))
              }
            }}
            onMouseDown={(e) => { dragRef.current = { x: e.clientX, panEnd } }}
            onMouseUp={() => { dragRef.current = null }}
            onDoubleClick={() => { setZoom(1); setPanEnd(0) }}
            onMouseLeave={() => { setHover(null); dragRef.current = null }}
            style={{ cursor: 'crosshair' }}
          >
            <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="block h-[210px] w-full">
              <defs>
                <linearGradient id={`fill-${symbol}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={lineColor} stopOpacity="0.25" />
                  <stop offset="100%" stopColor={lineColor} stopOpacity="0" />
                </linearGradient>
                <filter id={`glow-${symbol}`} x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur stdDeviation="2.4" result="b" />
                  <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
                </filter>
              </defs>
              {/* horizontal gridlines, quiet structure so the pane doesn't read as a naked line */}
              {[0.25, 0.5, 0.75].map((f) => (
                <line key={f} x1="0" y1={6 + f * (H - 12)} x2={W} y2={6 + f * (H - 12)} stroke={dark ? '#27272a' : '#e4e4e7'} strokeWidth="1" />
              ))}
              {prevClose != null && prevClose >= yMin && prevClose <= yMax && (
                <line x1="0" y1={Y(prevClose)} x2={W} y2={Y(prevClose)} stroke="#71717a" strokeWidth="1" strokeDasharray="4 4" />
              )}
              {/* Candlesticks: green when the candle closed up, red when it closed down */}
              {view.map((c, i) => {
                const cUp = c.c >= c.o
                const col = cUp ? '#34d399' : '#fb7185'
                const yO = Y(c.o), yC = Y(c.c)
                const bodyTop = Math.min(yO, yC)
                const bodyH = Math.max(1, Math.abs(yO - yC))
                return (
                  <g key={c.t}>
                    <line x1={cx(i)} y1={Y(c.h)} x2={cx(i)} y2={Y(c.l)} stroke={col} strokeWidth="1" strokeOpacity="0.85" />
                    <rect x={cx(i) - cw / 2} y={bodyTop} width={cw} height={bodyH} fill={col} rx="0.4" />
                  </g>
                )
              })}
              {/* Our 9 / 50 / 200 EMAs, same palette as the full chart (clamped so a far EMA hugs the edge) */}
              {viewEmas.map((e) => (allCloses.length > e.period ? (
                <path key={e.period} d={pathOfClamped(e.view)} fill="none" stroke={e.color} strokeWidth="1.5" strokeOpacity="0.95" strokeLinejoin="round" />
              ) : null))}
              {/* Hover crosshair */}
              {hover != null && view[hover] && (
                <>
                  <line x1={cx(hover)} y1="0" x2={cx(hover)} y2={H} stroke="#a1a1aa" strokeWidth="0.6" strokeDasharray="3 3" strokeOpacity="0.7" />
                  <line x1="0" y1={Y(view[hover].c)} x2={W} y2={Y(view[hover].c)} stroke="#a1a1aa" strokeWidth="0.6" strokeDasharray="3 3" strokeOpacity="0.5" />
                </>
              )}
            </svg>
            {/* price scale, session high/last/low pinned over the pane's right edge */}
            <span className="pointer-events-none absolute right-1.5 top-1 font-mono text-[10px] tabular-nums text-muted-foreground">${fmtPx(yMax)}</span>
            <span className="pointer-events-none absolute bottom-1 right-1.5 font-mono text-[10px] tabular-nums text-muted-foreground">${fmtPx(yMin)}</span>
            {lastClose != null && (
              <span className={`pointer-events-none absolute right-1.5 rounded px-1 py-px font-mono text-[10px] font-bold tabular-nums ${up ? 'bg-emerald-500/90 text-zinc-950' : 'bg-rose-500/90 text-zinc-950'}`}
                style={{ top: `calc(${((Y(lastClose) / H) * 100).toFixed(1)}% - 8px)` }}>
                ${fmtPx(lastClose)}
              </span>
            )}
            {prevClose != null && prevClose >= yMin && prevClose <= yMax && (
              <span className="pointer-events-none absolute left-1.5 font-mono text-[9px] text-muted-foreground"
                style={{ top: `calc(${((Y(prevClose) / H) * 100).toFixed(1)}% - 12px)` }}>
                prev ${fmtPx(prevClose)}
              </span>
            )}
            {/* Fixed top-left O/H/L/C legend on hover — always readable, never clipped (matches the daily chart). */}
            {(zoom !== 1 || panEnd !== 0) && (
              <button
                onClick={() => { setZoom(1); setPanEnd(0) }}
                className="absolute bottom-1.5 right-1.5 z-10 rounded-md border border-border bg-card/80 px-2 py-0.5 text-[10px] font-semibold text-foreground/80 backdrop-blur-sm hover:border-amber-500/50 hover:text-amber-700 dark:hover:text-amber-300"
              >
                Reset view
              </button>
            )}
            {/* Shared with the chart page (ChartStatsReadout) so the two cannot drift. Falls back to the
                LATEST bar when not hovering, so the row is informative before the cursor arrives rather
                than appearing only on hover. */}
            {(() => {
              const c = (hover != null ? view[hover] : view[view.length - 1]) as Bar | undefined
              if (!c) return null
              // Live 1m + 5m relative volume from the RAW 1-minute bars (this pulse displays 5-minute
              // candles, so the 1-minute read is the one it was missing). Shown side by side when not
              // hovering. Same math as the full chart. Customer request (support thread "RVOL - 5min").
              const liveRvol = (spanMin: number): { v: number; thin: boolean } | null => {
                const n = bars.length
                if (!n) return null
                let vol = 0, base = 0, minN = Infinity, any = false
                for (let i = Math.max(0, n - spanMin); i < n; i++) {
                  const b = bars[i]
                  if (b.baseVol && b.baseVol > 0 && Number.isFinite(b.v)) {
                    vol += b.v; base += b.baseVol; minN = Math.min(minN, b.baseN ?? 0); any = true
                  }
                }
                if (!any || base <= 0) return null
                return { v: vol / base, thin: (Number.isFinite(minN) ? minN : 0) < RVOL_MIN_SAMPLES }
              }
              return (
                <ChartStatsReadout
                  variant="bar"
                  className="mx-3 mb-1 border-0 bg-transparent px-0 py-0"
                  hovering={hover != null}
                  bar={{ t: c.t, open: c.o, high: c.h, low: c.l, close: c.c, volume: c.v }}
                  rvol={c.baseVol && c.baseVol > 0 ? { v: c.v / c.baseVol, thin: (c.baseN ?? 0) < 10 } : null}
                  rvol1m={liveRvol(1)}
                  rvol5m={liveRvol(5)}
                />
              )
            })()}
          </div>
          {/* Volume strip — bars colored by the candle's direction, aligned under the price pane */}
          <div className="relative border-t border-border">
            <svg viewBox={`0 0 ${W} 40`} preserveAspectRatio="none" className="block h-[40px] w-full">
              {view.map((c, i) => {
                const cUp = c.c >= c.o
                const vh = Math.max(0.6, (c.v / maxVol) * 36)
                return <rect key={c.t} x={cx(i) - cw / 2} y={40 - vh} width={cw} height={vh} fill={cUp ? '#34d399' : '#fb7185'} fillOpacity={hover === i ? 0.95 : 0.45} rx="0.4" />
              })}
            </svg>
            <span className="pointer-events-none absolute left-1.5 top-0.5 font-mono text-[9px] text-muted-foreground/70">Vol</span>
          </div>
          {/* RVOL strip — the same read as the full chart's RVOL pane, so the two cannot tell different
              stories about the same bar. Volume alone is unreadable without knowing what NORMAL is for
              this minute of this session; RVOL is that ratio, which is why it gets its own pane rather
              than being left to the hover readout. */}
          {rvolPane && (
            <div className="relative border-t border-border">
              <svg viewBox={`0 0 ${W} 62`} preserveAspectRatio="none" className="block h-[62px] w-full">
                {/* the 1.0x line: the whole point of the pane is "is this bar above or below normal" */}
                <line x1="0" y1={62 - (1 / rvolPane.cap) * 56} x2={W} y2={62 - (1 / rvolPane.cap) * 56}
                  stroke="#52525b" strokeWidth="1" strokeDasharray="3 3" />
                {view.map((c, i) => {
                  const r = rvolOf(c)
                  // A bar with no usable expectation draws NOTHING, not a zero. Most are the synthetic
                  // gap-fill candles the roll-up inserts on an illiquid name, so 0x would be an artifact
                  // of our own padding rather than a fact about the tape.
                  if (r == null) return null
                  const thin = (c.baseN ?? 0) < RVOL_MIN_SAMPLES
                  const col = thin ? 'rgba(113,113,122,0.45)'
                    : r >= 3 ? '#f43f5e'
                    : r >= 1 ? '#f59e0b'
                    : 'rgba(120,123,134,0.75)'
                  // Clipped at the pane top rather than rescaled: an 800x print reads as "off the scale"
                  // instead of squashing the 1-3x band, where nearly every bar lives, into two pixels.
                  const h = Math.max(0.6, Math.min(56, (r / rvolPane.cap) * 56))
                  return <rect key={c.t} x={cx(i) - cw / 2} y={62 - h} width={cw} height={h}
                    fill={col} fillOpacity={hover === i ? 1 : 0.75} rx="0.4" />
                })}
              </svg>
              <span className="pointer-events-none absolute left-1.5 top-0.5 font-mono text-[9px] text-muted-foreground/70">
                RVOL{' '}
                {(() => {
                  const c = (hover != null ? view[hover] : view[view.length - 1]) as Bar | undefined
                  const r = c ? rvolOf(c) : null
                  if (r == null) return <span className="text-muted-foreground/60">--</span>
                  const thin = ((c?.baseN ?? 0) < RVOL_MIN_SAMPLES)
                  return (
                    <span className={thin ? 'text-muted-foreground' : r >= 3 ? 'text-rose-600 dark:text-rose-400' : r >= 1 ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}>
                      {r >= 10 ? Math.round(r) : r.toFixed(1)}x{thin ? ' (thin)' : ''}
                    </span>
                  )
                })()}
              </span>
              {/* Pinned to the 1.0x line rather than the pane corner. At the corner it sat on top of the
                  bars AND described a line several pixels away, which is the one thing the label exists
                  to identify. */}
              <span className="pointer-events-none absolute right-1.5 -translate-y-1/2 bg-card/70 px-1 font-mono text-[9px] text-muted-foreground"
                style={{ top: `${(((62 - (1 / rvolPane.cap) * 56) / 62) * 100).toFixed(1)}%` }}>
                1.0x normal
              </span>
            </div>
          )}
          {/* Legend, same reading as the full chart */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border px-4 py-2">
            {viewEmas.map((e) => (allCloses.length > e.period ? (
              <span key={e.period} className="flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground">
                <span className="inline-block h-[3px] w-4 rounded-full" style={{ background: e.color }} />
                {e.period} EMA <span className="text-foreground">${fmtPx(e.view[e.view.length - 1])}</span>
              </span>
            ) : null))}
            <span className="ml-auto text-[10px] text-muted-foreground/70">5-min candles · last {view.length} · refreshes every 10s</span>
          </div>
        </>
      ) : (
        <div className="flex h-[210px] items-center justify-center text-sm text-muted-foreground/70">
          No recent price bars, the chart lights up when {symbol} starts printing.
        </div>
      )}
    </div>
  )
}
